'use client'

import React, { FC, ReactElement } from 'react'
import styles from './WalletActions.module.css'
import classNames from 'classnames'

import CopyButton from '@/components/copy-button'
import LockWalletIcon from '@/icons/lock-wallet'
import RemoveWalletIcon from '@/icons/remove-wallet'
import SignMessageIcon from '@/icons/sign-message'
import EyeIcon from '@/icons/eye'

import Wallet from '@/utils/wallet'
import Event from '@/utils/event'
import { removePassword } from '@/utils/password'
import useLoggedIn, { useLogout } from '@/utils/hooks/useLoggedIn'
import Link from '@/components/link'
import GearIcon from '@/icons/gear'
import { CDN_URL, STORAGE_PROFILE } from '@/utils/constants'
import UserIconStatic from '@/components/user-icon-static'
import abbreviateAddress from '@/utils/abbreviate-address'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

const WalletActions: FC<{
	className?: string
	toggleSignMessage: () => void
}> = (props): ReactElement => {
	const { className } = props
	const logout = useLogout()
	const _loggedIn = useLoggedIn()

	const _actions = [
		{
			icon: EyeIcon,
			label: 'View recovery',
			onClick: () => Event.emit('open-password-modal', { showSeed: true }),
			enabled: Wallet.type() === 'ordinalswallet'
		},
		{
			icon: SignMessageIcon,
			label: 'Sign message',
			onClick: props.toggleSignMessage,

			enabled: Wallet.type() === 'ordinalswallet'
		},
		{
			icon: LockWalletIcon,
			label: 'Lock wallet',
			onClick: () => {
				Event.emit('snackbar', { title: 'Wallet locked!' })
				removePassword()
			},
			enabled: Wallet.type() === 'ordinalswallet'
		},
		{
			icon: RemoveWalletIcon,
			label: 'Sign out',
			onClick: () => Event.emit('open-signout-modal'),
			enabled: Wallet.type() === 'ordinalswallet'
		},
		{
			icon: RemoveWalletIcon,
			label: 'Sign out',
			onClick: () => {
				logout()
			},
			enabled: Wallet.type() !== 'ordinalswallet'
		}
	].filter((e) => e.enabled)

	const _renderAction = React.useCallback(
		(action: any, index: any) => (
			<div
				className={classNames(styles.action, { [styles.actionRight]: index % 2 == 1 })}
				key={index}
				onClick={action.onClick}
				role="button"
				tabIndex={0}
				onKeyDown={onKeyboardClick(action.onClick)}
			>
				<action.icon className={styles.icon} />
				<p className={classNames(styles.label, 'regular', 'text-sm')}>{action.label}</p>
			</div>
		),
		[]
	)

	let icon
	try {
		let profile = localStorage.getItem(STORAGE_PROFILE)
		icon = JSON.parse(profile as any).icon
	} catch {}

	const address = Wallet.getPublicKey()
	const gradientId = parseInt(address, 16)

	return (
		<div className={classNames({ [props.className as any]: !!className }, styles.root)}>
			{icon ? (
				<UserIconStatic
					className={styles.profilePic}
					src={`${CDN_URL}/${icon}`}
					defaultGradientId={gradientId}
					size={30}
				/>
			) : (
				<UserIconStatic className={styles.profilePic} defaultGradientId={gradientId} size={30} />
			)}
			{!Wallet.provider.capabilities.includes('payments-wallet') && (
				<CopyButton
					className={styles.copyButton}
					value={Wallet.getTaprootAddress()}
					label={abbreviateAddress(Wallet.getTaprootAddress())}
					copiedClassName={classNames(styles.copied, 'text-sm')}
					tooltip="Copy to clipboard"
				/>
			)}
			<Link className={styles.settings} href="/settings">
				<GearIcon className={styles.gearIcon} />
			</Link>
		</div>
	)
}

export default WalletActions
